import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, FilePlus2, Calculator } from "lucide-react";
import { toast } from "sonner";

export const EUR = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

export const StatusPill = ({ status }) => (
  <span className={`text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${status === "approvato" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="quote-status">
    {status === "approvato" ? "Approvato" : "Bozza"}
  </span>
);

function NewQuote() {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const create = async (withAi) => {
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", { description: withAi ? text : "" }, { timeout: 120000 });
      nav(`/preventivi/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); setLoading(false); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="new-quote-box">
      <div className="flex items-center gap-2 font-display font-bold text-slate-900"><Sparkles className="w-4 h-4 text-violet-600" /> Nuovo preventivo</div>
      <p className="text-sm text-slate-500">Incolla la richiesta del cliente: ChatGPT stima persone, ore, furgoni, km e scatoloni, e il Listino calcola i costi. Poi controlli tu e approvi.</p>
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Messaggio, email o appunti della telefonata…" data-testid="new-quote-input" />
      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="outline" onClick={() => create(false)} disabled={loading} data-testid="new-quote-empty-button"><FilePlus2 className="w-4 h-4 mr-2" /> Preventivo vuoto</Button>
        <Button onClick={() => create(true)} disabled={loading || text.trim().length < 10} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="new-quote-ai-button">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {loading ? "ChatGPT sta preparando…" : "Prepara con ChatGPT"}
        </Button>
      </div>
    </section>
  );
}

export default function Quotes() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/quotes").then((r) => setList(r.data)); }, []);
  return (
    <div className="space-y-6" data-testid="quotes-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Commerciale</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Preventivi</h1>
          <p className="text-slate-500 mt-2 text-sm">Bozza → la controlli → approvi → PDF da mandare al cliente su WhatsApp.</p>
        </div>
        <Link to="/listino" className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:text-sky-900" data-testid="quotes-price-list-link">
          <Calculator className="w-4 h-4" /> Listino costi
        </Link>
      </header>
      <NewQuote />
      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="quotes-list">
        {list?.length === 0 && <div className="p-12 text-center text-slate-400">Nessun preventivo ancora.</div>}
        {list?.map((q) => (
          <Link key={q.id} to={`/preventivi/${q.id}`} className="flex flex-wrap items-center gap-3 p-4 hover:bg-slate-50 transition-colors" data-testid={`quote-row-${q.id}`}>
            <span className="font-mono text-xs text-slate-500">{q.number}</span>
            <span className="flex-1 min-w-[10rem] font-semibold text-slate-900">{q.client_name || "Cliente da inserire"}</span>
            <span className="text-sm text-slate-500">{q.scheduled_date || "data da concordare"}</span>
            <span className="font-semibold text-slate-900">{EUR.format(q.total || 0)}</span>
            <StatusPill status={q.status} />
          </Link>
        ))}
      </div>
    </div>
  );
}
